import { TFunction } from 'i18next';
import { CustomDetailData, CustomPersonalDetailData, Language, OrderData } from '../../../../api';
import { ExportCellValue, ExportColumn } from '../../../../common/export/exportTypes';

const readCustomValue = (bag: OrderData['custom_details'], key: string): ExportCellValue => {
  const values = bag as Record<string, any> | undefined | null;
  const value = values?.[key];
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return value as ExportCellValue;
};

/**
 * Columns are derived from the order page CONFIG, not from the keys present in the data.
 *
 * The previous implementation unioned whatever keys happened to appear across the orders, which made
 * column order depend on which order sorted first and silently dropped any configured field nobody
 * had filled in. Driving off the config — the same source the on-screen table uses — makes the export
 * stable between runs and keeps it matching what the admin sees.
 */
export const buildParticipantsExportColumns = (
  customPersonalDetails: CustomPersonalDetailData[],
  customDetails: CustomDetailData[],
  language: Language,
  t: TFunction,
): ExportColumn<OrderData>[] => [
  { key: 'returning_customer', header: t('returnningCustomer'), value: (order) => order.returning_customer },
  { key: 'firstname', header: t('export.firstname'), value: (order) => order.firstname },
  { key: 'lastname', header: t('export.lastname'), value: (order) => order.lastname },
  ...(customPersonalDetails ?? [])
    .filter((detail) => !detail.hide_from_console)
    .map((detail) => ({
      key: `cpd:${detail.id}`,
      // Same precedence and localization as the table header at EventParticipantsTable.tsx:205.
      header: (detail.console_label || detail.label)?.[language] || detail.id,
      value: (order: OrderData) => readCustomValue(order.custom_personal_details, detail.id),
    })),
  { key: 'persons', header: t('orders.num_persons'), value: (order) => order.persons },
  { key: 'order_number', header: t('orders.order_number'), value: (order) => order.order_number },
  { key: 'email', header: t('orders.email'), value: (order) => order.email },
  { key: 'phone', header: t('orders.phone'), value: (order) => order.phone },
  { key: 'comment', header: t('orders.comment'), value: (order) => order.comment },
  ...(customDetails ?? []).map((detail) => ({
    key: `cd:${detail.name}`,
    // label is optional on CustomDetailData, unlike CustomPersonalDetailData.
    header: detail.label?.[language] || detail.name,
    value: (order: OrderData) => readCustomValue(order.custom_details, detail.name),
  })),
];
